import { create } from "zustand";
import { devtools } from "zustand/middleware";

/**
 * Representa el resultado de sincronización de un registro individual
 */
export interface SyncResult {
  id: string;
  type: "sale" | "cancellation" | "refund";
  status: "pending" | "syncing" | "success" | "error";
  errorMessage?: string;
  errorCode?: number;
  timestamp: number;
  /** Datos adicionales para mostrar (ej: monto, cliente) */
  displayData?: {
    amount?: number;
    customerName?: string;
    documentType?: string;
  };
}

/**
 * Estado global del proceso de sincronización
 */
export interface SyncState {
  /** Si el proceso de sincronización está activo */
  isActive: boolean;
  /** Si el modal está minimizado */
  isMinimized: boolean;
  /** Si el modal está visible (puede estar minimizado pero visible como burbuja) */
  isVisible: boolean;
  /** Total de registros a sincronizar */
  totalCount: number;
  /** Registros procesados (exitosos + fallidos) */
  processedCount: number;
  /** Registros exitosos */
  successCount: number;
  /** Registros con error */
  errorCount: number;
  /** Lista detallada de resultados */
  results: SyncResult[];
  /** Mensaje de estado actual */
  statusMessage: string;
  /** Tiempo de inicio de la sincronización */
  startTime: number | null;
  /** Tiempo de finalización */
  endTime: number | null;
}

export interface SyncActions {
  /** Inicia un nuevo proceso de sincronización */
  startSync: (totalCount: number) => void;
  /** Actualiza el progreso con un resultado */
  addResult: (result: Omit<SyncResult, "timestamp">) => void;
  /** Marca un registro como "en proceso" */
  markSyncing: (id: string) => void;
  /** Minimiza el modal */
  minimize: () => void;
  /** Maximiza el modal */
  maximize: () => void;
  /** Cierra y resetea el monitor */
  close: () => void;
  /** Oculta después de completar (auto-hide) */
  hideAfterComplete: () => void;
  /** Calcula el porcentaje de progreso */
  getProgress: () => number;
  /** Verifica si el proceso está completo */
  isComplete: () => boolean;
}

const initialState: SyncState = {
  isActive: false,
  isMinimized: false,
  isVisible: false,
  totalCount: 0,
  processedCount: 0,
  successCount: 0,
  errorCount: 0,
  results: [],
  statusMessage: "",
  startTime: null,
  endTime: null,
};

export const useSyncStore = create<SyncState & SyncActions>()(
  devtools(
    (set, get) => ({
      ...initialState,

      startSync: (totalCount: number) => {
        set({
          isActive: true,
          isMinimized: false,
          isVisible: true,
          totalCount,
          processedCount: 0,
          successCount: 0,
          errorCount: 0,
          results: [],
          statusMessage: `Iniciando sincronización de ${totalCount} registro(s)...`,
          startTime: Date.now(),
          endTime: null,
        });
      },

      addResult: (result) => {
        const state = get();
        const fullResult: SyncResult = {
          ...result,
          timestamp: Date.now(),
        };

        const newResults = [...state.results];
        const existingIndex = newResults.findIndex((r) => r.id === result.id);

        if (existingIndex >= 0) {
          newResults[existingIndex] = fullResult;
        } else {
          newResults.push(fullResult);
        }

        const successCount =
          result.status === "success"
            ? state.successCount + 1
            : state.successCount;
        const errorCount =
          result.status === "error" ? state.errorCount + 1 : state.errorCount;
        const processedCount =
          result.status === "success" || result.status === "error"
            ? state.processedCount + 1
            : state.processedCount;

        const isComplete = processedCount >= state.totalCount;

        set({
          results: newResults,
          successCount,
          errorCount,
          processedCount,
          statusMessage: isComplete
            ? `Sincronización completada: ${successCount} exitosos, ${errorCount} con error`
            : `Sincronizando ${processedCount + 1} de ${state.totalCount}...`,
          endTime: isComplete ? Date.now() : null,
          isActive: !isComplete,
        });
      },

      markSyncing: (id: string) => {
        const state = get();
        const existingResult = state.results.find((r) => r.id === id);

        if (existingResult) {
          const newResults = state.results.map((r) =>
            r.id === id ? { ...r, status: "syncing" as const } : r
          );
          set({ results: newResults });
        } else {
          set({
            results: [
              ...state.results,
              {
                id,
                type: "sale",
                status: "syncing",
                timestamp: Date.now(),
              },
            ],
          });
        }
      },

      minimize: () => {
        set({ isMinimized: true });
      },

      maximize: () => {
        set({ isMinimized: false });
      },

      close: () => {
        set(initialState);
      },

      hideAfterComplete: () => {
        const state = get();
        if (state.processedCount >= state.totalCount) {
          // Auto-hide después de 3 segundos
          setTimeout(() => {
            set(initialState);
          }, 3000);
        }
      },

      getProgress: () => {
        const state = get();
        if (state.totalCount === 0) return 0;
        return Math.round((state.processedCount / state.totalCount) * 100);
      },

      isComplete: () => {
        const state = get();
        return state.processedCount >= state.totalCount && state.totalCount > 0;
      },
    }),
    { name: "sync-store" }
  )
);
